import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { ConciergeSettingsData, ConciergeTaskData } from 'src/lib/fliko/models/concierge/concierge.data';
import { DefectDefaults } from 'src/lib/fliko/models/defaults/defect.defaults';
import { DefectSettingsData } from 'src/lib/fliko/models/defects/defect-settings.data';
import { DefectData } from 'src/lib/fliko/models/defects/defect.data';
import { IssueHistoryRecordData } from 'src/lib/fliko/models/defects/issue-history.data';
import { IssueBase, IssueMessage, IssuePriority, IssueStatus, IssueType } from 'src/lib/fliko/models/defects/issue.base';
import { IssueData } from 'src/lib/fliko/models/defects/issue.data';
import { Collection } from 'src/lib/fliko/types/database.type';
import { Nullish } from 'src/lib/fliko/types/typeish.type';
import { UpdateIdOnly } from 'src/lib/fliko/types/update.type';
import { EditedImageVM } from 'src/app/shared/models/editem-image.view-model';
import { DatabaseService } from 'src/app/shared/services/database.service';
import { DbCollection } from 'src/app/shared/services/database/dbCollection';

@Injectable({
  providedIn: 'root'
})
export class IssueService {
  constructor(private dbSrv: DatabaseService) {}

  /**
   * @path /$issueType/$estateId/
   * @key id
   */
  private issues<T extends IssueBase>(type: IssueType): DbCollection<T> {
    return this.dbSrv.estateFactory.getCollection<T>(`${type}/$eId`);
  }

  private defect = this.issues<DefectData>(IssueType.DEFECT);
  private residentIssue = this.issues<IssueData>(IssueType.ISSUE);
  private conciergeTask = this.issues<ConciergeTaskData>(IssueType.CONCIERGE);

  // #region Universal

  public getIssue$(id: string, type: IssueType): Observable<IssueBase | Nullish> {
    return this.issues(type).getElement(id);
  }

  public setStatus(id: string, type: IssueType, status: IssueStatus): Promise<void> {
    return this.issues(type).updateElement(id, { status });
  }

  public setPriority(id: string, type: IssueType, priority: IssuePriority): Promise<void> {
    return this.issues(type).updateElement(id, { priority });
  }

  public assignToUser(issue: IssueBase, type: IssueType, assignedUserId: string): Promise<void> {
    const update = { assignedUserId, assignedUserDate: Date.now() };
    return this.issues(type).updateElement(issue.id, update);
  }

  public reportToFix(data: IssueBase, type: IssueType): Promise<void> {
    return this.issues(type).updateElement(data.id, this.patchStatus(data, { isReportToFix: true }));
  }

  public async deletePhoto(id: string, type: IssueType, deleted: EditedImageVM, estateId: string): Promise<void> {
    this.dbSrv.estateFactory.requireContext(estateId);
    const issue = await this.issues(type).snapshotElement(id);
    if (!issue?.photos) return;
    const photos = issue.photos
      .split('|')
      .filter((photo) => photo !== deleted.storagePath)
      .join('|');
    const issuePath = `${type}/${issue.estateId}/${id}`;
    const values: Record<string, unknown> = { [`${issuePath}/photos`]: photos };
    if (type === IssueType.DEFECT && deleted.originalPath) {
      const editedPhotos = { ...(issue as DefectData).editedPhotos };
      editedPhotos[deleted.originalPath] = null!; // HACK force delete the path
      values[`${issuePath}/editedPhotos`] = editedPhotos;
    }
    return this.dbSrv.estateFactory.updatePaths(issue.estateId, values);
  }

  public async deleteAttachment(id: string, type: IssueType, path: string, estateId: string): Promise<void> {
    this.dbSrv.estateFactory.requireContext(estateId);
    const issue = await this.issues(type).snapshotElement(id);
    if (!issue?.attachments) return;
    const attachments = issue.attachments.filter((att) => att !== path);
    return this.dbSrv.estateFactory.updatePaths(issue.estateId, {
      [`${type}/${issue.estateId}/${id}/attachments`]: attachments
    });
  }

  // #endregion

  // #region

  public getTasks$(): Observable<ConciergeTaskData[]> {
    return this.conciergeTask.getList();
  }

  public getTask$(id: string): Observable<ConciergeTaskData | Nullish> {
    return this.conciergeTask.getElement(id);
  }

  public setTask(data: ConciergeTaskData): Promise<void> {
    return this.conciergeTask.setElement(data.id, data);
  }

  public updateTask(data: UpdateIdOnly<ConciergeTaskData>): Promise<void> {
    return this.conciergeTask.updateElement(data.id, data);
  }

  // #endregion

  // #region Defects

  public getDefects$(): Observable<DefectData[]> {
    return this.defect.getList();
  }

  public setDefect(defect: DefectData): Promise<void> {
    return this.defect.setElement(defect.id, defect);
  }

  public updateDefect(defect: UpdateIdOnly<DefectData>): Promise<void> {
    return this.defect.updateElement(defect.id, defect);
  }

  public assignToSubcontractor(defect: DefectData, assignedSubcontractorId: string): Promise<void> {
    const update = { assignedSubcontractorId, assignmentDate: Date.now() };
    return this.defect.updateElement(defect.id, this.patchStatus(defect, update));
  }

  // #endregion Defects

  // #region Defect Settings
  /** @path /conciergeSettings/$estateId/$taskId/ */
  private conciergeSettings = this.dbSrv.estateFactory.getObject<ConciergeSettingsData>('conciergeSettings/$eId');

  public getConciergeSettings$(): Observable<ConciergeSettingsData | Nullish> {
    return this.conciergeSettings.get();
  }

  public async setConciergeSettings(data: ConciergeSettingsData): Promise<void> {
    await this.conciergeSettings.set(data);
  }

  /** @path /defectSettings/$estateId/ */
  private get defectSettings() {
    return this.dbSrv.estateFactory.getObject<DefectSettingsData>('defectSettings/$eId');
  }

  public getDefectSettings$(): Observable<DefectSettingsData> {
    return this.defectSettings.get().pipe(map((x) => x || DefectDefaults.SETTINGS()));
  }

  public getDefectSettingsOnce(cancel$?: Observable<unknown>): Promise<DefectSettingsData> {
    return this.defectSettings.snapshot(cancel$).then((x) => x ?? DefectDefaults.SETTINGS());
  }

  public updateDefectSettings(defectSettings: DefectSettingsData): Promise<void> {
    return this.defectSettings.update(defectSettings);
  }
  // #endregion

  // #region Defect Message
  /**
   * @param {string} issueId
   * @path /{$type}Message/$estateId/$issueId/
   * @key id
   */
  private messages = (type: IssueType, issueId: string) =>
    this.dbSrv.estateFactory.getCollection<IssueMessage>(`${type}Message/$eId/${issueId}`);

  /**
   * @path /{$type}Message/$estateId/
   * @key defectId - name for compatibility
   */
  private allMessages(type: IssueType) {
    return this.dbSrv.estateFactory.getCollection<Collection<IssueMessage>>(`${type}Message/$eId`);
  }

  public getMessages$(type: IssueType, issueId: string): Observable<IssueMessage[]> {
    return this.messages(type, issueId).getList();
  }

  public getAllDefectMessages$(): Observable<IssueMessage[]> {
    return this.allMessages(IssueType.DEFECT)
      .getObject()
      .pipe(map((obj) => ObjHlp.flatten<IssueMessage>(obj)));
  }

  public getAllConciergeMessages$(): Observable<Map<string, IssueMessage[]>> {
    return this.allMessages(IssueType.CONCIERGE)
      .getObject()
      .pipe(map((obj) => new Map(Object.entries(obj ?? {}).map(([key, val]) => [key, Array.from(Object.values(val))]))));
  }

  public setMessage(type: IssueType, message: IssueMessage): Promise<void> {
    return this.messages(type, message.defectId).setElement(message.id, message);
  }
  // #endregion Defect Message

  // #region Issues

  public getIssues$(): Observable<IssueData[]> {
    return this.residentIssue.getList();
  }

  public setIssue(issue: IssueData): Promise<void> {
    return this.residentIssue.setElement(issue.id, issue);
  }

  public updateIssue(issue: UpdateIdOnly<IssueData>): Promise<void> {
    return this.residentIssue.updateElement(issue.id, issue);
  }

  // #endregion

  // #region History

  /**
   * @param {string} issueId
   * @path /{$type}HistoryRecord/$estateId/$issueId/
   * @key id
   */
  private history = (type: IssueType, issueId: string) =>
    this.dbSrv.estateFactory.getCollectionReadOnly<IssueHistoryRecordData>(`${type}HistoryRecord/$eId/${issueId}`);

  public getHistoryRecords$(type: IssueType, issueId: string): Observable<IssueHistoryRecordData[]> {
    return this.history(type, issueId).getList();
  }

  // #endregion

  private patchStatus<T extends IssueBase>(oldData: T, updateFields: Partial<T>): Partial<T> {
    switch (oldData.status) {
      case IssueStatus.NOTICED:
      case IssueStatus.NONE:
      case undefined:
        updateFields.status = IssueStatus.IN_PROGRESS;
    }
    return updateFields;
  }
}
